import { ApiError } from "./api.js";

/**
 * Dig the *human* reason out of a dashboard-server error that wraps an
 * upstream portal-api failure.
 *
 * The support proxy (`proxyToSupport`, apps/server/src/routes/support.ts)
 * collapses any upstream 4xx to `{ error: "Support center returned 4xx",
 * code: "SUPPORT_CENTER_UPSTREAM_ERROR", details: { upstream_body: {...} } }`
 * — the only fields the web `api()` client keeps are `error` / `code` /
 * `details`, so the real message ("Invalid OTP", "Current password is
 * incorrect", a per-field validation list) lives under
 * `details.upstream_body`. Without this, callers show the opaque
 * "Support center returned 422" (the bug that left a locked-out user
 * staring at a meaningless code on the 2FA enrollment screen).
 *
 * Shared by the Profile change-password card, the mandatory 2FA enrollment
 * gate, and the admin "Manage 2FA" dialog so they all surface the same
 * honest message.
 */
export function upstreamErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = (e.details as { upstream_body?: unknown } | undefined)?.upstream_body as
      | { error?: unknown; details?: unknown }
      | undefined;
    const list = Array.isArray(body?.details)
      ? body.details.filter((x): x is string => typeof x === "string")
      : [];
    if (list.length) return list.join(". ");
    if (typeof body?.error === "string" && body.error.trim()) return body.error;
    if (e.message.trim()) return e.message;
  }
  return fallback;
}
